import { useEffect, useState } from "react";
import { PlusCircle } from "lucide-react";
import { v4 } from "uuid";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import t from "@/lang/lang";
import {
  CreateRequest,
  useProcessesProcessCreateMutation,
} from "@/services/backend/processes/api/process-api";
import {
  GSMServiceKind,
  ServiceKindList,
} from "@/services/backend/services/types/kinds";
import { Label } from "@/shared/components/ui/label";
import { useActiveCompany } from "@/shared/lib/active-company/active-company-provider";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  CommonDialogFooter,
  CommonDialogTrigger,
} from "@/shared/components/dialog/dialog-common";
import { ServiceKindSelect } from "@/services/backend/services/components/service-kind-select";
import {
  inputValidationErrorMessages,
  InputValidationErrors,
} from "@/shared/components/errors/input-error-messages";
import { TextInput } from "@/shared/components/form/text-input";
import { CustomerSelect } from "@/services/backend/customers/components/customer-select";
import { BranchesSelect } from "@/services/backend/branch/components/branches-select";
import { ServiceObjectsSelect } from "@/services/backend/customers/components/service-objects-select";
import { useServiceKindList } from "@/services/backend/services/hooks/use-service-kind-list";
import { useAuth } from "@/shared/lib/authorization/auth-context";

interface Props {
  onCreated: (processId: string, serviceKind: GSMServiceKind) => void;
}

export function CreateProcessDialog({ onCreated }: Props) {
  const defaultRequest = useDefaultRequest();
  const [open, setOpen] = useState(false);
  const [request, setRequest] = useState<CreateRequest>(defaultRequest);

  const [create, { isLoading, error, isSuccess, reset }] =
    useProcessesProcessCreateMutation();

  useEffect(() => {
    if (isSuccess) {
      toast.success("Prozess erstellt");
      setRequest(defaultRequest);
      setOpen(false);
      reset();
      onCreated(request.id, request.serviceKind as GSMServiceKind);
    }
  }, [
    defaultRequest,
    isSuccess,
    onCreated,
    request.id,
    request.serviceKind,
    reset,
  ]);

  const doCreate = () => {
    if (isLoading || request.branches.length === 0) {
      return;
    }

    // Empty optionals are omitted rather than sent: the backend treats a
    // present key as "set" and would write the zero value.
    create({
      ...request,
      topic: request.topic || undefined,
      customerId: request.customerId || undefined,
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <CommonDialogTrigger icon={PlusCircle} label={t("Vorgang")} size="sm" />
      <DialogContent className="sm:max-w-fit">
        <DialogHeader>
          <DialogTitle>{t("Neuen Vorgang anlegen")}</DialogTitle>
          <DialogDescription>
            {t(
              "Neue Vorgänge sollten hier ausschließlich angelegt werden, wenn es in MS Dynamics keine Verkaufschance gibt welche dem Vorgang entspricht. Ansonsten wird es unweigerlich zu Unklarheiten und Konflikten kommen.",
            )}
          </DialogDescription>
        </DialogHeader>
        <Fields request={request} setRequest={setRequest} error={error} />
        <RTKQueryErrorAlert error={error} ignoreValidationError={false} />
        <CommonDialogFooter
          isLoading={isLoading}
          onClickAction={doCreate}
          actionLabel={t("Erstellen")}
        />
      </DialogContent>
    </Dialog>
  );
}

function Fields({
  request,
  setRequest,
  error,
}: {
  request: CreateRequest;
  setRequest: (request: CreateRequest) => void;
  error: unknown;
}) {
  const serviceKinds = useServiceKindList();

  return (
    <div className="grid gap-4">
      <div className="grid content-start gap-1.5">
        <Label>{t("Dienstleistung")}</Label>
        <ServiceKindSelect
          value={request.serviceKind}
          onValueChange={(serviceKind) =>
            setRequest({
              ...request,
              serviceKind: serviceKind as GSMServiceKind,
            })
          }
          availableServiceKinds={serviceKinds as GSMServiceKind[]}
        />
        <InputValidationErrors error={error} field="serviceKind" />
      </div>
      <TextInput
        label={t("Thema")}
        field="topic"
        text={request.topic}
        onTextChange={(topic) => setRequest({ ...request, topic })}
        placeholder={t("Thema")}
        errors={inputValidationErrorMessages(error, "topic")}
      />
      <div className="grid content-start gap-1.5">
        <Label>{t("Kunde")}</Label>
        <CustomerSelect
          value={request.customerId}
          onValueChange={(customerId) => setRequest({ ...request, customerId })}
        />
        <InputValidationErrors error={error} field="customerId" />
      </div>
      {request.serviceKind !== GSMServiceKind.QPM &&
        request.serviceKind !== GSMServiceKind.GFS && (
          <div className="grid content-start gap-1.5">
            <Label>{t("Service Objekte")}</Label>
            <ServiceObjectsSelect
              customerId={request.customerId}
              value={request.serviceObjects}
              onValueChange={(serviceObjects) =>
                setRequest({ ...request, serviceObjects })
              }
              disabled={!request.customerId}
            />
            <InputValidationErrors error={error} field="serviceObjects" />
          </div>
        )}
      <div className="grid content-start gap-1.5">
        <Label>{t("Niederlassungen")}</Label>
        <BranchesSelect
          value={request.branches}
          onValueChange={(branches) => setRequest({ ...request, branches })}
          companyFilter={{ active: true, values: [request.companyId] }}
        />
        <InputValidationErrors error={error} field="branches" />
      </div>
    </div>
  );
}

function useDefaultRequest(): CreateRequest {
  const { companyId } = useActiveCompany();
  const { user } = useAuth();

  return {
    id: v4(),
    serviceKind: ServiceKindList[0] as GSMServiceKind,
    companyId,
    branches: [],
    // The user should always be there at this point.
    owningUserId: user!.id ?? "",
    topic: "",
    customerId: "",
    serviceObjects: [],
  };
}
